import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useIntl } from "react-intl";
import moment from "moment";
import { Button, Input, Typography } from "antd";
import { clientName, dimensionsText, formatAddress, money } from "../../mocks/carpets";
import { useCarpetPreview } from "./context";

const { Text } = Typography;
const { TextArea } = Input;

export default function Report() {
  const intl = useIntl();
  const location = useLocation();
  const { state } = useCarpetPreview();
  const stayIds = (location.state && location.state.stayIds) || [];
  const bundle = location.state && location.state.bundle;
  const [notes, setNotes] = useState("");

  const groups = useMemo(() => {
    const source = bundle || state;
    const stays = (bundle ? bundle.stays : state.stays).filter((stay) => stayIds.includes(stay.id));
    const byClient = new Map();
    stays.forEach((stay) => {
      const list = byClient.get(stay.clientId) || [];
      list.push(stay);
      byClient.set(stay.clientId, list);
    });
    return Array.from(byClient.entries()).map(([clientId, staysForClient]) => ({
      client: source.clients.find((client) => client.id === clientId),
      stays: staysForClient,
      carpets: source.carpets,
    }));
  }, [state, stayIds, bundle]);

  if (!stayIds.length) {
    return <Text>{intl.formatMessage({ id: "entries" })}</Text>;
  }

  return (
    <div>
      <div className="no-print" style={{ marginBottom: 12 }}>
        <Button type="primary" onClick={() => window.print()}>{intl.formatMessage({ id: "print" })}</Button>
      </div>
      <div style={{ marginBottom: 12, fontSize: 13 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{intl.formatMessage({ id: "generalNotes" })}</div>
        <TextArea
          className="no-print"
          autoSize
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
        {notes && <div className="print-only" style={{ whiteSpace: "pre-wrap" }}>{notes}</div>}
      </div>
      {groups.map(({ client, stays, carpets }) => (
        <div key={client.id} style={{ marginBottom: 10, pageBreakInside: "avoid" }}>
          <div style={{ fontSize: 13, lineHeight: 1.3, marginBottom: 4 }}>
            <strong>#{client.ref} {clientName(client)}</strong>
            {" · "}{client.phone}
            {" · "}{formatAddress(client.address)}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                {[ "ref", "category", "widthXLength", "storageBox", "cleanStatus", "estimatedPrice", "jobPrice", "inAt", "outAt" ].map((id) => (
                  <th key={id} style={id === "inAt" || id === "outAt" ? dateCell : head}>
                    {intl.formatMessage({ id })}
                  </th>
                ))}
                {[ "inNotes", "outNotes" ].map((id) => (
                  <th key={id} className="no-print" style={{ border: "1px solid #d9d9d9", padding: "4px 6px", textAlign: "left" }}>
                    {intl.formatMessage({ id })}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stays.map((stay) => {
                const carpet = carpets.find((item) => item.id === stay.carpetId);
                const noteLine = [
                  stay.inNotes && `${intl.formatMessage({ id: "inNotes" })}: ${stay.inNotes}`,
                  stay.outNotes && `${intl.formatMessage({ id: "outNotes" })}: ${stay.outNotes}`,
                ].filter(Boolean).join(" · ");
                return (
                  <React.Fragment key={stay.id}>
                    <tr>
                      <td style={cell}>{carpet.ref}</td>
                      <td style={cell}>{intl.formatMessage({ id: carpet.category })}</td>
                      <td style={cell}>{dimensionsText(carpet)}</td>
                      <td style={cell}>{stay.storageBox}</td>
                      <td style={{ ...cell, textTransform: "uppercase" }}>{intl.formatMessage({ id: stay.cleaned ? "cleaned" : "dirty" })}</td>
                      <td style={cell}>{money(carpet.price)}</td>
                      <td style={cell}>{money(stay.jobPrice)}</td>
                      <td style={dateCell}>{moment(stay.inAt).format("DD/MM/YYYY HH:mm")}</td>
                      <td style={dateCell}>{stay.outAt ? moment(stay.outAt).format("DD/MM/YYYY HH:mm") : ""}</td>
                      <td className="no-print" style={cell}>{stay.inNotes}</td>
                      <td className="no-print" style={cell}>{stay.outNotes}</td>
                    </tr>
                    {noteLine && (
                      <tr className="print-only-row">
                        <td colSpan={9} style={{ ...cell, borderTop: "none" }}>{noteLine}</td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

const head = { border: "1px solid #d9d9d9", padding: "4px 6px", textAlign: "left" };
const cell = { border: "1px solid #d9d9d9", padding: "4px 6px", verticalAlign: "top" };
const dateCell = { ...cell, width: 140, minWidth: 140, whiteSpace: "nowrap" };
